import { useDispatch, useSelector } from 'react-redux';
import {
  useSendManualSalesforceRequestPayloadMutation,
  useSendManualTransferPayloadMutation,
  useSendYodleeManualTransferPayloadMutation,
  useSendYodleeSalesforceRequestPayloadMutation,
  useSendYodleeTransferPayloadMutation,
} from 'src/api/atv/atvApi';
import { AccountType, HoldingTransferType, TransferTypeValue } from 'src/utils/constants';
import { calculateBalanceFromHoldings } from 'src/utils/numberUtils';
import { RootState } from '../../../state/store';
import { TransferFlowStep } from '../steps';
import { setCurrentStep, setDocusignUrl } from '../transferSlice';

const handleTransferResponse = (dispatch: any) => (response: any) => {
  if (response && response.isMailInFormRequired) {
    dispatch(setDocusignUrl(response.ceremonyUrl));
    dispatch(setCurrentStep(TransferFlowStep.REQUIRES_PAPERWORK));
  } else if (response && response.ceremonyUrl) {
    window.location.href = response.ceremonyUrl;
  }
  return response;
};

const getAssetTransferType = (transferType: TransferTypeValue) =>
  transferType === TransferTypeValue.FullTransfer ? 'FULL' : 'PARTIAL';

/** Shared selectors for Yodlee-based transfer payloads */
const useYodleeTransferSelectors = () => {
  const transferType = useSelector((state: RootState) => state.transfer.transferType);
  const sessionId = useSelector((state: RootState) => state.yodleeConfig.uuid);
  const providerAccountId = useSelector(
    (state: RootState) => state.yodleeConfig.providerAccountID,
  );
  const accountId = useSelector(
    (state: RootState) => state.transfer.selectedExternalAccount?.selectedId,
  );
  const internalAccount = useSelector(
    (state: RootState) => state.transfer.selectedInternalAccount,
  );
  const selectedHoldings = useSelector(
    (state: RootState) => state.transfer.selectedHoldings,
  );
  const externalAccountBalance = useSelector(
    (state: RootState) => state.transfer.selectedExternalAccount?.balance,
  );

  const transferAmount =
    transferType === TransferTypeValue.FullTransfer
      ? (externalAccountBalance?.amount ?? 0)
      : calculateBalanceFromHoldings(
          selectedHoldings,
          false,
          transferType === TransferTypeValue.PartialTransfer,
          externalAccountBalance,
        ).amount;

  return {
    transferType,
    sessionId,
    providerAccountId,
    accountId,
    internalAccount,
    selectedHoldings,
    externalAccountBalance,
    transferAmount,
  };
};

export const useSendYodleeTransferPayload = () => {
  const dispatch = useDispatch();
  const [sendYodleeTransferPayload] = useSendYodleeTransferPayloadMutation();
  const {
    transferType,
    sessionId,
    providerAccountId,
    accountId,
    internalAccount,
    selectedHoldings,
    transferAmount,
  } = useYodleeTransferSelectors();

  return async () => {
    const assetTransferType = getAssetTransferType(transferType);
    const agreementId = internalAccount?.selectedId;
    const payload: any = {
      externalAccount: {
        sessionId,
        providerAccountId,
        accountId,
        balance: {
          amount: transferAmount,
        },
      },
      agreementId,
      transferType: assetTransferType,
    };

    if (selectedHoldings && transferType === TransferTypeValue.PartialTransfer) {
      payload.externalAccount.holdings = selectedHoldings.map((holding) => ({
        symbol: holding.symbol,
        quantity: holding.quantity,
        isAll: holding.transferType === HoldingTransferType.TOTAL,
      }));
    }

    return sendYodleeTransferPayload(payload)
      .unwrap()
      .then(handleTransferResponse(dispatch))
      .catch((error) => {
        throw error;
      });
  };
};

export const useSendManualTransferPayload = () => {
  const dispatch = useDispatch();
  const [sendManualTransferPayload] = useSendManualTransferPayloadMutation();
  const transferType = useSelector((state: RootState) => state.transfer.transferType);
  const manualExternalAccount = useSelector(
    (state: RootState) => state.transfer.manualExternalAccount,
  );
  const manualSelectedHoldings = useSelector(
    (state: RootState) => state.transfer.manualSelectedHoldings,
  );
  const internalAccount = useSelector(
    (state: RootState) => state.transfer.selectedInternalAccount,
  );

  return async () => {
    const assetTransferType = getAssetTransferType(transferType);
    const agreementId = internalAccount?.selectedId;

    const payload: any = {
      externalAccount: {
        providerAccountName: manualExternalAccount.institutionName,
        accountNumber: manualExternalAccount.accountNumber,
        accountType:
          manualExternalAccount.accountType !== null
            ? AccountType[manualExternalAccount.accountType]
            : null,
        primaryOwnerName: manualExternalAccount.ownerName,
        balance: {
          amount: manualExternalAccount.balance?.amount ?? 0,
        },
        ...(manualExternalAccount.jointOwnerName &&
          manualExternalAccount.accountType === AccountType.JOINT_TENANT && {
            jointOwnerName: manualExternalAccount.jointOwnerName,
          }),
      },
      agreementId,
      transferType: assetTransferType,
    };

    if (manualSelectedHoldings && transferType === TransferTypeValue.PartialTransfer) {
      payload.externalAccount.holdings = manualSelectedHoldings
        .filter((holding) => holding.restored)
        .map((holding) => ({
          symbol: holding.symbol,
          quantity: holding.quantity,
          isAll: holding.transferType === HoldingTransferType.TOTAL,
        }));
    }

    return sendManualTransferPayload(payload)
      .unwrap()
      .then(handleTransferResponse(dispatch))
      .catch((error) => {
        throw error;
      });
  };
};

export const useSendYodleeManualTransferPayload = () => {
  const dispatch = useDispatch();
  const [sendYodleeManualTransferPayload] = useSendYodleeManualTransferPayloadMutation();
  const {
    transferType,
    sessionId,
    providerAccountId,
    accountId,
    internalAccount,
    selectedHoldings,
    transferAmount,
  } = useYodleeTransferSelectors();

  const externalAccountIncomplete = useSelector(
    (state: RootState) => state.transfer.externalAccountIncomplete,
  );

  const missingFields = useSelector(
    (state: RootState) => state.transfer.selectedExternalAccount?.missingFields,
  );

  return async () => {
    const payload: any = {
      externalAccount: {
        sessionId,
        providerAccountId,
        accountId,
        balance: { amount: transferAmount },
      },
      agreementId: internalAccount?.selectedId,
      transferType: getAssetTransferType(transferType),
    };

    if (externalAccountIncomplete && missingFields) {
      payload.externalAccount.updatedFields = Object.entries(externalAccountIncomplete)
        .filter(
          ([key, value]) =>
            missingFields.includes(key) && value !== null && value !== undefined,
        )
        .reduce<Record<string, unknown>>((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
    }

    if (selectedHoldings && transferType === TransferTypeValue.PartialTransfer) {
      payload.externalAccount.holdings = selectedHoldings.map((holding) => ({
        symbol: holding.symbol,
        quantity: holding.quantity,
        isAll: holding.transferType === HoldingTransferType.TOTAL,
      }));
    }

    return sendYodleeManualTransferPayload(payload)
      .unwrap()
      .then(handleTransferResponse(dispatch))
      .catch((error) => {
        throw error;
      });
  };
};

export const useSendYodleeSalesforceRequestPayload = () => {
  const [sendYodleeSalesforceRequestPayload] =
    useSendYodleeSalesforceRequestPayloadMutation();
  const sessionId = useSelector((state: RootState) => state.yodleeConfig.uuid);
  const providerAccountId = useSelector(
    (state: RootState) => state.yodleeConfig.providerAccountID,
  );
  const accountId = useSelector(
    (state: RootState) => state.transfer.selectedExternalAccount?.selectedId,
  );

  return async () => {
    const payload: any = {
      processType: 'YODLEE',
      activityStatus: 'ABANDONED',
      externalAccount: {
        sessionId,
        providerAccountId,
        accountId,
      },
    };

    return sendYodleeSalesforceRequestPayload(payload).unwrap();
  };
};

export const useSendManualSalesforceRequestPayload = () => {
  const [sendManualSalesforceRequestPayload] =
    useSendManualSalesforceRequestPayloadMutation();
  const manualExternalAccount = useSelector(
    (state: RootState) => state.transfer.manualExternalAccount,
  );

  return async () => {
    const payload: any = {
      processType: 'MANUAL',
      activityStatus: 'ABANDONED',
      providerAccountName: manualExternalAccount?.institutionName,
      product: AccountType[manualExternalAccount?.accountType as AccountType],
      balance: { amount: manualExternalAccount?.balance?.amount },
    };

    return sendManualSalesforceRequestPayload(payload).unwrap();
  };
};
